import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Platform, TextInput } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Text, Title } from '@constants/Themed';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialCommunityIcons, AntDesign } from '@expo/vector-icons';
import apis from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';

const AttendanceManagement = () => {
  const theme = themeSelector();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateRange, setDateRange] = useState<'day' | 'week' | 'month'>('day');

  // Calculate date range based on selection
  const getDateRange = () => {
    const start = new Date(selectedDate);
    const end = new Date(selectedDate);
    
    if (dateRange === 'week') {
      const dayOfWeek = start.getDay();
      start.setDate(start.getDate() - dayOfWeek);
      end.setDate(start.getDate() + 6);
    } else if (dateRange === 'month') {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    }
    
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  };

  const { startDate, endDate } = getDateRange();

  const { data: attendanceData, isLoading } = useQuery({
    queryKey: ['hrAttendance', currentCompany?._id, startDate, endDate],
    queryFn: async () => {
      const response = await apis.getAttendance({ startDate, endDate });
      return response?.data || [];
    },
    enabled: !!currentCompany?._id
  });

  const recordAttendanceMutation = useMutation({
    mutationFn: (data: { employeeId: string; date: string; checkIn?: string; checkOut?: string; status?: string }) =>
      apis.recordAttendance(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hrAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['hrDashboard'] });
    }
  });

  const attendance = attendanceData || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present': return '#10b981';
      case 'absent': return '#ef4444';
      case 'late': return '#f59e0b';
      case 'half-day': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  return (
    <DefaultScreen>
      <View style={styles.container}>
        <View style={styles.header}>
          <Title style={{ fontSize: 28, fontWeight: '700', color: theme ? '#111827' : '#f9fafb' }}>
            Attendance Management
          </Title>
          <Text style={{ fontSize: 14, color: theme ? '#6b7280' : '#9ca3af', marginTop: 4 }}>
            {dateRange === 'day' 
              ? new Date(selectedDate).toLocaleDateString()
              : dateRange === 'week'
              ? `Week of ${new Date(startDate).toLocaleDateString()}`
              : new Date(selectedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
            }
          </Text>
        </View>

        {/* Date Range Selector */}
        <View style={styles.dateRangeContainer}>
          <TouchableOpacity
            style={[
              styles.dateRangeButton,
              dateRange === 'day' && { backgroundColor: '#3b82f6' }
            ]}
            onPress={() => setDateRange('day')}
          >
            <Text style={[styles.dateRangeText, dateRange === 'day' && { color: '#ffffff' }]}>
              Day
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.dateRangeButton,
              dateRange === 'week' && { backgroundColor: '#3b82f6' }
            ]}
            onPress={() => setDateRange('week')}
          >
            <Text style={[styles.dateRangeText, dateRange === 'week' && { color: '#ffffff' }]}>
              Week
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.dateRangeButton,
              dateRange === 'month' && { backgroundColor: '#3b82f6' }
            ]}
            onPress={() => setDateRange('month')}
          >
            <Text style={[styles.dateRangeText, dateRange === 'month' && { color: '#ffffff' }]}>
              Month
            </Text>
          </TouchableOpacity>
        </View>

        {/* Date Picker */}
        <View style={[styles.datePickerContainer, { backgroundColor: theme ? '#f3f4f6' : '#374151' }]}>
          <AntDesign name="calendar" size={20} color={theme ? '#6b7280' : '#9ca3af'} />
          <TextInput
            style={[styles.dateInput, { color: theme ? '#111827' : '#f9fafb' }]}
            value={selectedDate}
            onChangeText={setSelectedDate}
            placeholder="YYYY-MM-DD"
            placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
          />
        </View>

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <Text>Loading...</Text>
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false}>
            {attendance.map((record: any) => {
              const employee = record.employee || record.user || {};
              const employeeName = employee.profile_information?.name 
                ? `${employee.profile_information.name} ${employee.profile_information.surname || ''}`.trim()
                : employee.name || 'Unknown Employee';
              
              return (
              <View
                key={record._id || record.id}
                style={[
                  styles.recordCard,
                  {
                    backgroundColor: theme ? '#ffffff' : '#1f2937',
                    borderColor: theme ? '#e5e7eb' : '#374151'
                  }
                ]}
              >
                <View style={styles.recordHeader}>
                  <View style={styles.recordInfo}>
                    <Text style={[styles.employeeName, { color: theme ? '#111827' : '#f9fafb' }]}>
                      {employeeName}
                    </Text>
                    <Text style={[styles.recordDate, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                      {new Date(record.date).toLocaleDateString()}
                    </Text>
                    {record.checkIn && (
                      <View style={styles.timeRow}>
                        <MaterialCommunityIcons name="login" size={16} color={theme ? '#10b981' : '#34d399'} />
                        <Text style={[styles.recordTime, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                          {new Date(record.checkIn).toLocaleTimeString()}
                        </Text>
                      </View>
                    )}
                    {record.checkOut && (
                      <View style={styles.timeRow}>
                        <MaterialCommunityIcons name="logout" size={16} color={theme ? '#ef4444' : '#f87171'} />
                        <Text style={[styles.recordTime, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                          {new Date(record.checkOut).toLocaleTimeString()}
                        </Text>
                      </View>
                    )}
                  </View>
                  <View style={[
                    styles.statusBadge,
                    { backgroundColor: `${getStatusColor(record.status)}20` }
                  ]}>
                    <Text style={[
                      styles.statusText,
                      { color: getStatusColor(record.status) }
                    ]}>
                      {record.status || 'present'}
                    </Text>
                  </View>
                </View>
              </View>
            );
            })}
            {attendance.length === 0 && (
              <View style={styles.emptyContainer}>
                <MaterialCommunityIcons name="clock-outline" size={48} color={theme ? '#9ca3af' : '#6b7280'} />
                <Text style={[styles.emptyText, { color: theme ? '#6b7280' : '#9ca3af' }]}>
                  No attendance records for this date
                </Text>
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </DefaultScreen>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20
  },
  header: {
    marginBottom: 24
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40
  },
  recordCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12
  },
  recordHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  recordInfo: {
    flex: 1
  },
  recordDate: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8
  },
  recordTime: {
    fontSize: 14,
    marginBottom: 4
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize'
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center'
  },
  emptyText: {
    fontSize: 16,
    marginTop: 12
  },
  dateRangeContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16
  },
  dateRangeButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#e5e7eb'
  },
  dateRangeText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827'
  },
  datePickerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    marginBottom: 20,
    gap: 12
  },
  dateInput: {
    flex: 1,
    fontSize: 16
  },
  employeeName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4
  },
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4
  }
});

export default AttendanceManagement;

